/**
 * BootScene - Handles asset loading and initialization
 *
 * This scene runs first and:
 * 1. Loads the command-deck display fonts via WebFontLoader
 * 2. Loads overhaul spritesheets and UI assets
 * 3. Displays loading progress
 * 4. Transitions to TitleScene when complete
 */

import Phaser from 'phaser';
import WebFont from 'webfontloader';
import {
  COLORS,
  COLOR_CSS,
  FONTS,
  GAME_WIDTH,
  GAME_HEIGHT,
  ENTITY_PIXELS,
  DURATIONS,
} from '../config';
import { audioSystem } from '../systems/AudioSystem';
import { saveSystem } from '../systems/SaveSystem';
import { logSceneEvent } from '../utils/automation';
import { WEAPON_CARD_TEXTURE_KEYS, WEAPON_GUN_TEXTURE_KEYS } from '../ui/weaponVisuals';

export class BootScene extends Phaser.Scene {
  private loadingText!: Phaser.GameObjects.Text;
  private progressBar!: Phaser.GameObjects.Graphics;
  private progressBox!: Phaser.GameObjects.Graphics;
  private fontsLoaded: boolean = false;
  private assetsLoaded: boolean = false;

  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    this.createLoadingUI();
    this.loadFonts();
    this.loadOverhaulAssets();
  }

  create(): void {
    logSceneEvent('BootScene');
    // Initialize save system (loads saved settings)
    saveSystem.load();

    // Set up audio system unlock listener for mobile browsers
    audioSystem.setupUnlockListener(this);

    // Create animations from loaded spritesheets
    this.createAnimations();

    // Check if everything is loaded
    this.checkLoadingComplete();
  }

  /**
   * Creates the loading progress UI elements
   */
  private createLoadingUI(): void {
    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2;
    const barWidth = 300;
    const barHeight = 30;

    // Progress bar background box
    this.progressBox = this.add.graphics();
    this.progressBox.fillStyle(COLORS.bgTrack, 0.8);
    this.progressBox.fillRoundedRect(
      centerX - barWidth / 2 - 5,
      centerY - barHeight / 2 - 5,
      barWidth + 10,
      barHeight + 10,
      8
    );

    // Progress bar fill
    this.progressBar = this.add.graphics();

    // Loading text - use system font initially since custom fonts aren't loaded yet
    this.loadingText = this.add.text(centerX, centerY - 50, 'Loading...', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '24px',
      color: COLOR_CSS.uiText,
    }).setOrigin(0.5);

    // Update progress bar as assets load
    this.load.on('progress', (value: number) => {
      this.updateProgress(value * 0.5); // Assets are 50% of total
    });

    this.load.on('complete', () => {
      this.assetsLoaded = true;
      this.createFallbackSprites();
      this.checkLoadingComplete();
    });
  }

  /**
   * Updates the progress bar display
   */
  private updateProgress(percent: number): void {
    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2;
    const barWidth = 300;
    const barHeight = 30;

    this.progressBar.clear();
    this.progressBar.fillStyle(COLORS.player, 1);
    this.progressBar.fillRoundedRect(
      centerX - barWidth / 2,
      centerY - barHeight / 2,
      barWidth * percent,
      barHeight,
      6
    );

    const percentText = Math.floor(percent * 100);
    this.loadingText.setText(`Loading... ${percentText}%`);
  }

  /**
   * Loads Google Fonts using WebFontLoader
   */
  private loadFonts(): void {
    WebFont.load({
      google: {
        families: [
          'Oxanium:wght@500;600;700;800',
          'Rajdhani:wght@500;600;700',
          'Share Tech Mono',
        ],
      },
      active: () => {
        // Fonts loaded successfully
        this.fontsLoaded = true;
        this.updateProgress(1); // 100% complete
        this.checkLoadingComplete();
      },
      inactive: () => {
        // Font loading failed - continue anyway with fallback fonts
        console.warn('Web fonts failed to load. Using fallback fonts.');
        this.fontsLoaded = true;
        this.updateProgress(1);
        this.checkLoadingComplete();
      },
      timeout: 5000, // 5 second timeout
    });
  }

  /**
   * Loads overhaul spritesheets and UI assets
   */
  private loadOverhaulAssets(): void {
    this.load.image(
      'title-battle-bg',
      '/assets/overhaul/backgrounds/title-battle-bg-v1.webp'
    );

    // Character spritesheets (48x48 frames)
    this.load.spritesheet('soldier', '/assets/overhaul/sheets/soldier-sheet-48.png', {
      frameWidth: 48,
      frameHeight: 48,
    });
    this.load.spritesheet('enemy-basic', '/assets/overhaul/sheets/lobster-sheet-48.png', {
      frameWidth: 48,
      frameHeight: 48,
    });
    this.load.spritesheet('enemy-elite', '/assets/overhaul/sheets/lobster-sheet-48.png', {
      frameWidth: 48,
      frameHeight: 48,
    });

    // UI elements (derived from ui-sheet)
    for (let i = 0; i <= 9; i++) {
      this.load.image(`digit-${i}`, `/assets/overhaul/ui/digit-${i}.png`);
    }
    this.load.image('bullet-red', '/assets/overhaul/ui/bullet-red.png');
    this.load.image('gate-panel', '/assets/overhaul/ui/gate-panel.png');
    this.load.image('icon-pause', '/assets/overhaul/ui/icon-pause.png');
    this.load.image('icon-settings', '/assets/overhaul/ui/icon-gear.png');

    for (const key of WEAPON_CARD_TEXTURE_KEYS) {
      this.load.image(key, `/assets/overhaul/ui/${key}.png`);
    }
    for (const key of WEAPON_GUN_TEXTURE_KEYS) {
      this.load.image(key, `/assets/overhaul/ui/${key}.png`);
    }
  }

  /**
   * Creates fallback sprites for assets not included in the overhaul pack
   */
  private createFallbackSprites(): void {
    this.createFriendlyBullet();
    this.createFallbackWeaponBox();
    this.createFallbackRocket();
    this.createUIIcons();
    this.createWeaponIcons();
  }

  private createFriendlyBullet(): void {
    if (this.textures.exists('bullet-friendly')) return;

    const graphics = this.make.graphics({ x: 0, y: 0 }, false);
    graphics.fillStyle(0x00D4FF, 0.2);
    graphics.fillRoundedRect(1, 0, 5, 14, 2);
    graphics.fillStyle(0x73F7FF, 0.7);
    graphics.fillRoundedRect(2, 1, 3, 12, 1);
    graphics.fillStyle(0xFFFFFF, 1);
    graphics.fillRect(3, 2, 1, 8);
    graphics.generateTexture('bullet-friendly', 7, 14);
    graphics.destroy();
  }

  private createAnimations(): void {
    if (!this.anims.exists('soldier_idle')) {
      this.anims.create({
        key: 'soldier_idle',
        frames: this.anims.generateFrameNumbers('soldier', { start: 0, end: 7 }),
        frameRate: 6,
        repeat: -1,
      });
      this.anims.create({
        key: 'soldier_walk',
        frames: this.anims.generateFrameNumbers('soldier', { start: 8, end: 15 }),
        frameRate: 10,
        repeat: -1,
      });
      this.anims.create({
        key: 'soldier_shoot',
        frames: this.anims.generateFrameNumbers('soldier', { start: 16, end: 23 }),
        frameRate: 12,
        repeat: -1,
      });
      this.anims.create({
        key: 'soldier_death',
        frames: this.anims.generateFrameNumbers('soldier', { start: 24, end: 31 }),
        frameRate: 10,
        repeat: 0,
      });
    }

    if (!this.anims.exists('lobster_idle')) {
      this.anims.create({
        key: 'lobster_idle',
        frames: this.anims.generateFrameNumbers('enemy-basic', { start: 0, end: 4 }),
        frameRate: 6,
        repeat: -1,
      });
      this.anims.create({
        key: 'lobster_walk',
        frames: this.anims.generateFrameNumbers('enemy-basic', { start: 5, end: 8 }),
        frameRate: 8,
        repeat: -1,
      });
      this.anims.create({
        key: 'lobster_hit',
        frames: this.anims.generateFrameNumbers('enemy-basic', { start: 10, end: 13 }),
        frameRate: 10,
        repeat: 0,
      });
      this.anims.create({
        key: 'lobster_death',
        frames: this.anims.generateFrameNumbers('enemy-basic', { start: 15, end: 18 }),
        frameRate: 10,
        repeat: 0,
      });
    }
  }

  private createFallbackWeaponBox(): void {
    if (this.textures.exists('weapon-box')) return;

    const boxSize = ENTITY_PIXELS.weaponBox;
    const boxGraphics = this.make.graphics({ x: 0, y: 0 }, false);
    const notch = 8;
    // Shadow
    boxGraphics.fillStyle(COLORS.goldDark, 1);
    boxGraphics.beginPath();
    boxGraphics.moveTo(notch + 2, 2);
    boxGraphics.lineTo(boxSize - notch + 2, 2);
    boxGraphics.lineTo(boxSize + 2, notch + 2);
    boxGraphics.lineTo(boxSize + 2, boxSize - notch + 2);
    boxGraphics.lineTo(boxSize - notch + 2, boxSize + 2);
    boxGraphics.lineTo(notch + 2, boxSize + 2);
    boxGraphics.lineTo(2, boxSize - notch + 2);
    boxGraphics.lineTo(2, notch + 2);
    boxGraphics.closePath();
    boxGraphics.fillPath();
    // Main body
    boxGraphics.fillStyle(COLORS.gold, 1);
    boxGraphics.beginPath();
    boxGraphics.moveTo(notch, 0);
    boxGraphics.lineTo(boxSize - notch, 0);
    boxGraphics.lineTo(boxSize, notch);
    boxGraphics.lineTo(boxSize, boxSize - notch);
    boxGraphics.lineTo(boxSize - notch, boxSize);
    boxGraphics.lineTo(notch, boxSize);
    boxGraphics.lineTo(0, boxSize - notch);
    boxGraphics.lineTo(0, notch);
    boxGraphics.closePath();
    boxGraphics.fillPath();
    // Top highlight
    boxGraphics.fillStyle(0xFFFFFF, 0.3);
    boxGraphics.beginPath();
    boxGraphics.moveTo(notch + 4, 4);
    boxGraphics.lineTo(boxSize - notch - 4, 4);
    boxGraphics.lineTo(boxSize - notch - 4, boxSize / 3);
    boxGraphics.lineTo(notch + 4, boxSize / 3);
    boxGraphics.closePath();
    boxGraphics.fillPath();
    // Border
    boxGraphics.lineStyle(2, COLORS.goldDark, 0.8);
    boxGraphics.beginPath();
    boxGraphics.moveTo(notch, 0);
    boxGraphics.lineTo(boxSize - notch, 0);
    boxGraphics.lineTo(boxSize, notch);
    boxGraphics.lineTo(boxSize, boxSize - notch);
    boxGraphics.lineTo(boxSize - notch, boxSize);
    boxGraphics.lineTo(notch, boxSize);
    boxGraphics.lineTo(0, boxSize - notch);
    boxGraphics.lineTo(0, notch);
    boxGraphics.closePath();
    boxGraphics.strokePath();
    boxGraphics.generateTexture('weapon-box', boxSize + 4, boxSize + 4);
    boxGraphics.destroy();
  }

  private createFallbackRocket(): void {
    if (this.textures.exists('rocket')) return;

    const rocketGraphics = this.make.graphics({ x: 0, y: 0 }, false);
    rocketGraphics.fillStyle(COLORS.gold, 0.3);
    rocketGraphics.fillEllipse(8, 12, 8, 12);
    rocketGraphics.fillStyle(COLORS.gold, 1);
    rocketGraphics.fillEllipse(8, 12, 6, 10);
    rocketGraphics.fillStyle(COLORS.enemy, 1);
    rocketGraphics.fillEllipse(8, 5, 4, 5);
    rocketGraphics.fillStyle(0xFFFFFF, 0.6);
    rocketGraphics.fillEllipse(8, 10, 2, 4);
    rocketGraphics.generateTexture('rocket', 16, 24);
    rocketGraphics.destroy();
  }

  /**
   * Creates simple UI icon textures
   */
  private createUIIcons(): void {
    // Pause icon - always regenerate to avoid malformed source texture collisions.
    if (this.textures.exists('icon-pause')) {
      this.textures.remove('icon-pause');
    }
    const pauseGraphics = this.make.graphics({ x: 0, y: 0 }, false);
    pauseGraphics.fillStyle(COLORS.uiText, 1);
    pauseGraphics.fillRoundedRect(13, 9, 7, 26, 2);
    pauseGraphics.fillRoundedRect(24, 9, 7, 26, 2);
    pauseGraphics.generateTexture('icon-pause', 44, 44);
    pauseGraphics.destroy();

    // Play icon - triangle pointing right
    const playGraphics = this.make.graphics({ x: 0, y: 0 }, false);
    playGraphics.fillStyle(COLORS.uiText, 1);
    playGraphics.fillTriangle(10, 8, 10, 32, 34, 20);
    playGraphics.generateTexture('icon-play', 44, 40);
    playGraphics.destroy();

    // Settings/gear icon - simplified gear shape
    if (!this.textures.exists('icon-settings')) {
      const settingsGraphics = this.make.graphics({ x: 0, y: 0 }, false);
      settingsGraphics.fillStyle(COLORS.uiText, 1);
      settingsGraphics.fillCircle(22, 22, 14);
      settingsGraphics.fillStyle(COLORS.bgGameplay, 1);
      settingsGraphics.fillCircle(22, 22, 6);
      settingsGraphics.generateTexture('icon-settings', 44, 44);
      settingsGraphics.destroy();
    }

    // Lock icon - padlock shape
    if (!this.textures.exists('icon-lock')) {
      const lockGraphics = this.make.graphics({ x: 0, y: 0 }, false);
      lockGraphics.fillStyle(COLORS.uiTextSecondary, 1);
      lockGraphics.fillRoundedRect(8, 18, 28, 22, 4);
      lockGraphics.lineStyle(4, COLORS.uiTextSecondary, 1);
      lockGraphics.strokeCircle(22, 14, 8);
      lockGraphics.generateTexture('icon-lock', 44, 44);
      lockGraphics.destroy();
    }

    // Checkmark icon
    if (!this.textures.exists('icon-check')) {
      const checkGraphics = this.make.graphics({ x: 0, y: 0 }, false);
      checkGraphics.lineStyle(4, COLORS.uiSuccess, 1);
      checkGraphics.beginPath();
      checkGraphics.moveTo(10, 22);
      checkGraphics.lineTo(18, 30);
      checkGraphics.lineTo(34, 12);
      checkGraphics.strokePath();
      checkGraphics.generateTexture('icon-check', 44, 44);
      checkGraphics.destroy();
    }

    // Star icon - simple 5-point star
    if (!this.textures.exists('icon-star')) {
      const starGraphics = this.make.graphics({ x: 0, y: 0 }, false);
      starGraphics.fillStyle(COLORS.gold, 1);
      this.drawStar(starGraphics, 22, 22, 5, 18, 9);
      starGraphics.generateTexture('icon-star', 44, 44);
      starGraphics.destroy();
    }

    // Sound on icon
    if (!this.textures.exists('icon-sound-on')) {
      const soundOnGraphics = this.make.graphics({ x: 0, y: 0 }, false);
      soundOnGraphics.fillStyle(COLORS.uiText, 1);
      soundOnGraphics.fillTriangle(10, 14, 10, 30, 22, 22);
      soundOnGraphics.fillRect(8, 17, 6, 10);
      soundOnGraphics.lineStyle(2, COLORS.uiText, 1);
      soundOnGraphics.beginPath();
      soundOnGraphics.arc(22, 22, 8, -0.8, 0.8, false);
      soundOnGraphics.strokePath();
      soundOnGraphics.beginPath();
      soundOnGraphics.arc(22, 22, 14, -0.6, 0.6, false);
      soundOnGraphics.strokePath();
      soundOnGraphics.generateTexture('icon-sound-on', 44, 44);
      soundOnGraphics.destroy();
    }

    // Sound off icon
    if (!this.textures.exists('icon-sound-off')) {
      const soundOffGraphics = this.make.graphics({ x: 0, y: 0 }, false);
      soundOffGraphics.fillStyle(COLORS.uiTextSecondary, 1);
      soundOffGraphics.fillTriangle(10, 14, 10, 30, 22, 22);
      soundOffGraphics.fillRect(8, 17, 6, 10);
      soundOffGraphics.lineStyle(3, COLORS.enemy, 1);
      soundOffGraphics.lineBetween(28, 14, 38, 30);
      soundOffGraphics.lineBetween(38, 14, 28, 30);
      soundOffGraphics.generateTexture('icon-sound-off', 44, 44);
      soundOffGraphics.destroy();
    }

    // Music on icon - music note
    if (!this.textures.exists('icon-music-on')) {
      const musicOnGraphics = this.make.graphics({ x: 0, y: 0 }, false);
      musicOnGraphics.fillStyle(COLORS.uiText, 1);
      musicOnGraphics.fillEllipse(14, 30, 8, 6);
      musicOnGraphics.fillRect(20, 10, 3, 22);
      musicOnGraphics.lineStyle(3, COLORS.uiText, 1);
      musicOnGraphics.beginPath();
      musicOnGraphics.moveTo(23, 10);
      musicOnGraphics.lineTo(32, 14);
      musicOnGraphics.lineTo(32, 20);
      musicOnGraphics.strokePath();
      musicOnGraphics.generateTexture('icon-music-on', 44, 44);
      musicOnGraphics.destroy();
    }

    // Music off icon - music note with X
    if (!this.textures.exists('icon-music-off')) {
      const musicOffGraphics = this.make.graphics({ x: 0, y: 0 }, false);
      musicOffGraphics.fillStyle(COLORS.uiTextSecondary, 1);
      musicOffGraphics.fillEllipse(12, 30, 7, 5);
      musicOffGraphics.fillRect(17, 12, 3, 20);
      musicOffGraphics.lineStyle(2, COLORS.uiTextSecondary, 1);
      musicOffGraphics.beginPath();
      musicOffGraphics.moveTo(20, 12);
      musicOffGraphics.lineTo(27, 15);
      musicOffGraphics.lineTo(27, 20);
      musicOffGraphics.strokePath();
      musicOffGraphics.lineStyle(3, COLORS.enemy, 1);
      musicOffGraphics.lineBetween(30, 14, 40, 28);
      musicOffGraphics.lineBetween(40, 14, 30, 28);
      musicOffGraphics.generateTexture('icon-music-off', 44, 44);
      musicOffGraphics.destroy();
    }

  }

  /**
   * Creates polished weapon icon textures for the HUD
   */
  private createWeaponIcons(): void {
    const variants = ['v1', 'v2', 'v3', 'v4', 'v5'] as const;
    const cardSize = 192;
    const gunWidth = 192;
    const gunHeight = 88;

    const gunPalette = {
      v1: 0x69bf5a,
      v2: 0x53a8ff,
      v3: 0x78d85d,
      v4: 0xa36cf0,
      v5: 0xf08f45,
    } as const;

    for (const variant of variants) {
      const cardKey = `weapon-card-${variant}`;
      if (!this.textures.exists(cardKey)) {
        const cardGraphics = this.make.graphics({ x: 0, y: 0 }, false);
        const notch = 26;

        cardGraphics.fillStyle(0xe8ce49, 1);
        cardGraphics.beginPath();
        cardGraphics.moveTo(notch, 0);
        cardGraphics.lineTo(cardSize - notch, 0);
        cardGraphics.lineTo(cardSize, notch);
        cardGraphics.lineTo(cardSize, cardSize - notch);
        cardGraphics.lineTo(cardSize - notch, cardSize);
        cardGraphics.lineTo(notch, cardSize);
        cardGraphics.lineTo(0, cardSize - notch);
        cardGraphics.lineTo(0, notch);
        cardGraphics.closePath();
        cardGraphics.fillPath();

        cardGraphics.lineStyle(3, 0xa58833, 0.95);
        cardGraphics.strokePath();
        cardGraphics.generateTexture(cardKey, cardSize, cardSize);
        cardGraphics.destroy();
      }

      const gunKey = `weapon-gun-${variant}`;
      if (!this.textures.exists(gunKey)) {
        const gunGraphics = this.make.graphics({ x: 0, y: 0 }, false);
        const gunColor = gunPalette[variant];
        gunGraphics.fillStyle(gunColor, 1);
        gunGraphics.fillRoundedRect(18, 26, 130, 28, 6);
        gunGraphics.fillRoundedRect(44, 52, 34, 24, 4);
        gunGraphics.fillRoundedRect(134, 34, 42, 12, 4);
        gunGraphics.fillRoundedRect(58, 14, 48, 12, 4);
        gunGraphics.fillStyle(0xffffff, 0.28);
        gunGraphics.fillRoundedRect(22, 30, 90, 8, 4);
        gunGraphics.fillStyle(0x111111, 0.7);
        gunGraphics.fillRoundedRect(150, 36, 24, 8, 2);
        gunGraphics.generateTexture(gunKey, gunWidth, gunHeight);
        gunGraphics.destroy();
      }
    }

    // Soldier icon for HUD counter - with glow and highlight
    if (!this.textures.exists('icon-soldier')) {
      const soldierIconGraphics = this.make.graphics({ x: 0, y: 0 }, false);
      soldierIconGraphics.fillStyle(COLORS.player, 0.3);
      soldierIconGraphics.fillCircle(16, 16, 14);
      soldierIconGraphics.fillStyle(COLORS.player, 1);
      soldierIconGraphics.fillCircle(16, 16, 11);
      soldierIconGraphics.fillStyle(0xFFFFFF, 0.4);
      soldierIconGraphics.fillCircle(13, 13, 5);
      soldierIconGraphics.generateTexture('icon-soldier', 32, 32);
      soldierIconGraphics.destroy();
    }
  }

  /**
   * Draws a star shape
   */
  private drawStar(
    graphics: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    spikes: number,
    outerRadius: number,
    innerRadius: number
  ): void {
    let rot = Math.PI / 2 * 3;
    const step = Math.PI / spikes;

    graphics.beginPath();
    graphics.moveTo(cx, cy - outerRadius);

    for (let i = 0; i < spikes; i++) {
      const x1 = cx + Math.cos(rot) * outerRadius;
      const y1 = cy + Math.sin(rot) * outerRadius;
      graphics.lineTo(x1, y1);
      rot += step;

      const x2 = cx + Math.cos(rot) * innerRadius;
      const y2 = cy + Math.sin(rot) * innerRadius;
      graphics.lineTo(x2, y2);
      rot += step;
    }
    graphics.lineTo(cx, cy - outerRadius);
    graphics.closePath();
    graphics.fillPath();
  }

  /**
   * Checks if all loading is complete and transitions to next scene
   */
  private checkLoadingComplete(): void {
    if (this.fontsLoaded && this.assetsLoaded) {
      // Show completion message with proper fonts
      this.loadingText.setFontFamily(FONTS.display);
      this.loadingText.setText('Ready!');

      // Brief delay before transition so player sees "Ready!"
      this.time.delayedCall(300, () => {
        this.transitionToTitle();
      });
    }
  }

  /**
   * Handles transition to TitleScene with fade effect
   */
  private transitionToTitle(): void {
    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);

    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('TitleScene');
    });
  }
}
